// frontend/src/utils/cacheManager.ts
/**
 * Centralized Cache Manager
 * Ensures consistent cache clearing across the app
 * Fixes E4: Browser Cache/Context issues
 */

import { invalidateDashboardCache } from './dashboardDataCache';
import logger from './logger';

// Keys that should be cleared on logout/user switch
const USER_SCOPED_STORAGE_KEYS = [
  // Auth
  'loggedIn',
  'userPlan',
  'userId',
  'userEmail',
  'fallbackSession',
  'smSessionToken',
  'pendingLoginToken',
  'pendingLoginTime',

  // Backend-owned Founder settings and compatibility projections
  'user_settings',
  'user_tier',
  'founder_role',
  'journey_exploration_mode',
  
  // Dashboard state
  'xprojex_first_touch',
  'xprojex_last_touch',
  'dashboard_tab',
  'selected_project',
  'journey_view_mode',
  'intelligence_journey_mode',
  
  // Journey/Onboarding
  'onboarding_completed',
  'onboarding_tooltips_seen',
  'stage_celebrations_shown',
  
  // User preferences that should reset
  'weekly_goals_collapsed',
  'ai_recommendations_visible',
];

// Keys to preserve across sessions (non-user-specific)
const PRESERVED_KEYS = [
  'a11y-preferences', // Accessibility settings
  'theme', // Dark/light mode
  'language', // Language preference
  'xprojex_tracking_consent', // GDPR consent
  'update_banner_dismissed', // Version-specific
];

/**
 * Clear all user-scoped cache and storage
 * Call this on logout or user switch
 */
export function clearUserCache(): void {
  logger.info('[CacheManager] Clearing user-scoped cache');
  
  // 1. Clear in-memory dashboard cache
  try {
    invalidateDashboardCache();
  } catch (err) {
    logger.warn('[CacheManager] Could not invalidate dashboard cache:', err);
  }
  
  // 2. Clear localStorage (user-scoped keys only)
  USER_SCOPED_STORAGE_KEYS.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (err) {
      // Ignore errors for non-existent keys
    }
  });
  
  // 3. Clear sessionStorage completely (session-specific)
  try {
    sessionStorage.clear();
  } catch (err) {
    logger.warn('[CacheManager] Could not clear sessionStorage:', err);
  }
  
  // 4. Clear any cached API responses (if using service worker)
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => {
        if (name.includes('api-cache') || name.includes('user-data')) {
          caches.delete(name);
        }
      });
    }).catch(() => {});
  }
  
  logger.info('[CacheManager] User cache cleared successfully');
}

/**
 * Clear all cache (use sparingly - e.g., version update)
 */
export function clearAllCache(): void {
  logger.info('[CacheManager] Clearing ALL cache');
  
  // Save preserved keys
  const preserved: Record<string, string | null> = {};
  PRESERVED_KEYS.forEach(key => {
    preserved[key] = localStorage.getItem(key);
  });
  
  // Clear everything
  localStorage.clear();
  sessionStorage.clear();
  
  // Restore preserved keys
  Object.entries(preserved).forEach(([key, value]) => {
    if (value !== null) {
      localStorage.setItem(key, value);
    }
  });
  
  // Clear service worker caches
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => caches.delete(name));
    }).catch(() => {});
  }
  
  logger.info('[CacheManager] All cache cleared');
}

/**
 * Ensure fresh context for new users
 * Call this when user lands without existing session
 */
export function ensureFreshContext(): void {
  const hasSession = localStorage.getItem('loggedIn') === 'true';
  const hasUserId = !!localStorage.getItem('userId');
  
  // If no valid session, clear any stale user data
  if (!hasSession || !hasUserId) {
    logger.info('[CacheManager] No valid session, ensuring fresh context');
    clearUserCache();
  }
}

/**
 * Scope storage key with user ID to prevent cross-user contamination
 */
export function getUserScopedKey(baseKey: string, userId?: string): string {
  const uid = userId || localStorage.getItem('userId') || 'anonymous';
  return `${uid}:${baseKey}`;
}

const cacheManager = {
  clearUserCache,
  clearAllCache,
  ensureFreshContext,
  getUserScopedKey,
};

export default cacheManager;
